import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { accountTransactionsReportAPI, fundAPI } from '../../services/api';
import { Download, ArrowLeft, FileSpreadsheet, Loader2, Calendar, Search } from 'lucide-react';

const fmtAmount = (n) => (n == null ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const AccountTransactionsReport = () => {
  const [funds, setFunds] = useState([]);
  const [fundFilter, setFundFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [lastRange, setLastRange] = useState({ from: 'since_inception', to: '' });

  useEffect(() => {
    fundAPI.getAll().then(res => setFunds(res.data)).catch(() => setFunds([]));
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await accountTransactionsReportAPI.get({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        fundId: fundFilter || undefined,
      });
      setRows(res.data?.rows || []);
      setLastRange({ from: res.data?.from_date || 'since_inception', to: res.data?.to_date || '' });
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* initial load = since inception */ /* eslint-disable-next-line */ }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await accountTransactionsReportAPI.export({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        fundId: fundFilter || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Account_Transactions_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const filtered = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return ['Account Name', 'Fund Name', 'Folio No.', 'PPM No.', 'Type', 'Class', 'ISIN', 'Remarks']
      .some(k => String(r[k] || '').toLowerCase().includes(q));
  });

  const total = filtered.reduce((s, r) => s + (Number(r.Amount) || 0), 0);

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto" data-testid="account-txn-report-page">
      {/* Header */}
      <Link to="/investors/reports" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3" data-testid="account-txn-back">
        <ArrowLeft size={14} className="mr-1" /> Back to Reports
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-blue-600" /> Investor Account-wise Transactions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Bulk export of every transaction posted under an Investor Account&apos;s Transaction tab.
            Showing range: <strong>{lastRange.from === 'since_inception' ? 'Since Inception' : lastRange.from}</strong> &rarr; <strong>{lastRange.to || 'today'}</strong>
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || filtered.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          data-testid="account-txn-export-btn"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export to Excel
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5" data-testid="account-txn-filter-bar">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-gray-400" />
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="w-full text-sm border rounded-lg px-2 py-1.5"
                data-testid="account-txn-from-date" />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Empty = Since Inception</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-gray-400" />
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="w-full text-sm border rounded-lg px-2 py-1.5"
                data-testid="account-txn-to-date" />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Empty = Today. Transactions after this date are excluded.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fund (optional)</label>
            <select value={fundFilter} onChange={e => setFundFilter(e.target.value)}
              className="w-full text-sm border rounded-lg px-2 py-1.5"
              data-testid="account-txn-fund-filter">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={fetchRows} className="w-full px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium" data-testid="account-txn-apply-btn">
              Apply Filters
            </button>
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search account, fund, folio, type, class, ISIN, remarks…"
            className="w-full text-sm border rounded-lg pl-9 pr-3 py-1.5"
            data-testid="account-txn-search"
          />
        </div>
      </div>

      {/* Summary tile */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-blue-50 rounded-lg p-3" data-testid="account-txn-count-tile">
          <p className="text-xs text-blue-600 font-medium">Transactions in Range</p>
          <p className="text-xl font-bold text-blue-900">{filtered.length.toLocaleString()}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3" data-testid="account-txn-sum-tile">
          <p className="text-xs text-green-600 font-medium">Sum of Amount</p>
          <p className="text-xl font-bold text-green-900">{fmtAmount(total)}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3">
          <p className="text-xs text-amber-600 font-medium">Range</p>
          <p className="text-sm font-bold text-amber-900">
            {lastRange.from === 'since_inception' ? 'Since Inception' : lastRange.from}
            <span className="text-amber-700 mx-1">→</span>
            {lastRange.to || 'today'}
          </p>
        </div>
      </div>

      {/* Results table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 size={20} className="animate-spin inline mr-2" />Loading transactions…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No transactions found in the selected range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {['Fund Name', 'Currency', 'Folio No.', 'PPM No.', 'Account Name', 'Date', 'Type', 'Amount',
                    'Class', 'ISIN', 'Units Issued', 'Type of Class', 'Total Drawdown', 'Remarks'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r, idx) => (
                  <tr key={idx} className="hover:bg-blue-50/30" data-testid={`account-txn-row-${idx}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{r['Fund Name']}</td>
                    <td className="px-3 py-2">{r['Currency']}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['Folio No.']}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['PPM No.']}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{r['Account Name']}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['Date']}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['Type']}</td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmtAmount(r['Amount'])}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['Class']}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['ISIN']}</td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmtAmount(r['Units Issued'])}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r['Type of Class']}</td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmtAmount(r['Total Drawdown'])}</td>
                    <td className="px-3 py-2 max-w-[18rem] truncate" title={r['Remarks'] || ''}>{r['Remarks']}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountTransactionsReport;
